import React, { useState } from 'react';

const assetData = [
  {
    type: 'Established House (Metro)',
    stage: 'Early to Mid Journey',
    pros: 'Strong capital growth, Renovation potential, Easier to rent',
    cons: 'Lower yield, Older condition, More maintenance',
    cashFlow: 2,
    growth: 4,
    complexity: 2,
    depositPercent: 20,
    bestFor: 'Long-term growth investors',
  },
  {
    type: 'New House & Land Package',
    stage: 'Early Journey',
    pros: 'Depreciation benefits, Low maintenance, First Home Buyer perks',
    cons: 'Weaker growth in outer suburbs, Delayed build risk',
    cashFlow: 2,
    growth: 2,
    complexity: 2,
    depositPercent: 10,
    bestFor: 'Beginners, cash flow-conscious buyers',
  },
  {
    type: 'Dual Occupancy',
    stage: 'Mid Journey',
    pros: 'High yield, Multiple incomes, Can live in one, rent other',
    cons: 'Council approvals, Limited capital growth',
    cashFlow: 4,
    growth: 2,
    complexity: 3,
    depositPercent: 20,
    bestFor: 'Income-focused or downsizers',
  },
  {
    type: 'Units/Apartments (High Density)',
    stage: 'Avoid or only for affordability',
    pros: 'Affordable entry, Central locations',
    cons: 'Poor capital growth, Strata fees, Oversupply risk',
    cashFlow: 1,
    growth: 1,
    complexity: 2,
    depositPercent: 20,
    bestFor: 'Budget-constrained buyers',
  },
  {
    type: 'Regional Houses',
    stage: 'Early to Mid Journey',
    pros: 'Higher rental yields, Affordable entry, Value-add potential',
    cons: 'Market volatility, Liquidity risk, Slower capital growth',
    cashFlow: 4,
    growth: 3,
    complexity: 2,
    depositPercent: 20,
    bestFor: 'Yield-focused, low-deposit investors',
  },
  {
    type: 'Commercial Property',
    stage: 'Late Journey',
    pros: 'High rental returns, Long leases, Outgoings paid by tenant',
    cons: 'Large deposits, Vacancy risk, Financing harder',
    cashFlow: 4,
    growth: 2,
    complexity: 4,
    depositPercent: 30,
    bestFor: 'Experienced investors nearing retirement',
  },
  {
    type: 'Subdividable Blocks',
    stage: 'Mid to Late Journey',
    pros: 'Uplift potential, Future development, Can hold or sell lots',
    cons: 'Complex DD, Development risk, Capital intensive',
    cashFlow: 2,
    growth: 4,
    complexity: 4,
    depositPercent: 25,
    bestFor: 'Strategic investors or developers',
  },
  {
    type: 'Boarding Houses / NDIS / Co-living',
    stage: 'Late or Specialized',
    pros: 'Exceptional yield, Positive cash flow, Niche demand',
    cons: 'Regulatory risk, High management, Exit value unknown',
    cashFlow: 5,
    growth: 2,
    complexity: 4,
    depositPercent: 30,
    bestFor: 'Yield-driven sophisticated investors',
  },
  {
    type: 'Reno Flip',
    stage: 'Mid Journey',
    pros: 'Quick equity uplift, Value add through renovation, Short holding period',
    cons: 'High stamp duty and selling costs, Reno timeline risk, CGT payable on sale',
    cashFlow: 2,
    growth: 3,
    complexity: 3,
    depositPercent: 25,
    bestFor: 'Experienced investors or builders looking for active strategy',
  },
];

const getColor = (percent) => {
  if (percent <= 15) return 'bg-green-100 text-green-800';
  if (percent <= 25) return 'bg-yellow-100 text-yellow-800';
  return 'bg-red-100 text-red-800';
};

const PropertyAssetMatrix = () => {
  const [stageFilter, setStageFilter] = useState('All');
  const [cashFlowFilter, setCashFlowFilter] = useState('All');
  const [growthFilter, setGrowthFilter] = useState('All');
  const [complexityFilter, setComplexityFilter] = useState('All');

  const uniqueStages = ['All', ...new Set(assetData.map((a) => a.stage))];

  const filteredData = assetData.filter((a) => {
    return (
      (stageFilter === 'All' || a.stage === stageFilter) &&
      (cashFlowFilter === 'All' || a.cashFlow === Number(cashFlowFilter)) &&
      (growthFilter === 'All' || a.growth === Number(growthFilter)) &&
      (complexityFilter === 'All' || a.complexity === Number(complexityFilter))
    );
  });

  return (
    <div className="overflow-x-auto p-4">
      <h2 className="text-xl font-bold mb-4">📊 Property Investment Asset Matrix</h2>

      <div className="flex flex-wrap gap-4 mb-4">
        <div>
          <label className="mr-2 font-medium">Stage:</label>
          <select value={stageFilter} onChange={(e) => setStageFilter(e.target.value)} className="border p-1 rounded">
            {uniqueStages.map((stage, i) => (
              <option key={i} value={stage}>{stage}</option>
            ))}
          </select>
        </div>

        <div>
          <label className="mr-2 font-medium">Cash Flow:</label>
          <select value={cashFlowFilter} onChange={(e) => setCashFlowFilter(e.target.value)} className="border p-1 rounded">
            {['All', 1, 2, 3, 4, 5].map((v) => <option key={v} value={v}>{v}</option>)}
          </select>
        </div>

        <div>
          <label className="mr-2 font-medium">Growth:</label>
          <select value={growthFilter} onChange={(e) => setGrowthFilter(e.target.value)} className="border p-1 rounded">
            {['All', 1, 2, 3, 4, 5].map((v) => <option key={v} value={v}>{v}</option>)}
          </select>
        </div>

        <div>
          <label className="mr-2 font-medium">Complexity:</label>
          <select value={complexityFilter} onChange={(e) => setComplexityFilter(e.target.value)} className="border p-1 rounded">
            {['All', 1, 2, 3, 4, 5].map((v) => <option key={v} value={v}>{v}</option>)}
          </select>
        </div>
      </div>

      <table className="min-w-full border text-sm">
        <thead>
          <tr className="bg-gray-100">
            <th className="p-2 border">Asset Type</th>
            <th className="p-2 border">When to Buy</th>
            <th className="p-2 border">Pros</th>
            <th className="p-2 border">Cons</th>
            <th className="p-2 border">Cash Flow (1–5)</th>
            <th className="p-2 border">Growth (1–5)</th>
            <th className="p-2 border">Complexity (1–5)</th>
            <th className="p-2 border">Deposit Required (%)</th>
            <th className="p-2 border">Best For</th>
          </tr>
        </thead>
        <tbody>
          {filteredData.map((asset, idx) => (
            <tr key={idx} className="border-b">
              <td className="p-2 border font-medium">{asset.type}</td>
              <td className="p-2 border">{asset.stage}</td>
              <td className="p-2 border">{asset.pros}</td>
              <td className="p-2 border">{asset.cons}</td>
              <td className="p-2 border text-center">{asset.cashFlow}</td>
              <td className="p-2 border text-center">{asset.growth}</td>
              <td className="p-2 border text-center">{asset.complexity}</td>
              <td className={`p-2 border text-center font-semibold ${getColor(asset.depositPercent)}`}>{asset.depositPercent}%</td>
              <td className="p-2 border">{asset.bestFor}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};

export default PropertyAssetMatrix;